import { useId, useState } from 'react';
import { CITIES, CITY_DISTRICTS, canonicalCity, cityFromCoords } from '@/data/geo';
import { requestUserLocation } from '@/sdk/telegram';

interface Props {
  city: string;
  district: string;
  onCity: (v: string) => void;
  onDistrict: (v: string) => void;
  /** Подписи над полями — в профиле они есть, в форме отчёта используется плейсхолдер. */
  labels?: boolean;
  districtPlaceholder?: string;
  /** Кнопка «определить по геолокации». Нужна в форме отчёта, в профиле избыточна. */
  detectable?: boolean;
}

/**
 * Город и район с автокомплитом. Нативный `<datalist>` даёт выпадающий список, поиск по
 * подстроке и клавиатурную навигацию во всех вебвью Telegram, оставаясь обычным `<input>` —
 * незнакомый город (справочник неполон) по-прежнему можно ввести руками.
 */
export function LocationFields({
  city, district, onCity, onDistrict, labels = false, districtPlaceholder = 'Район',
  detectable = false,
}: Props) {
  const uid = useId();
  const cityListId = `${uid}-cities`;
  const districtListId = `${uid}-districts`;
  // Район зависит от города, и для незнакомого города подсказок просто нет.
  const districts = CITY_DISTRICTS[canonicalCity(city)] ?? [];

  const [detecting, setDetecting] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  // Координаты никуда не отправляются и нигде не сохраняются: из них выводится только
  // название города, которое курьер и так вводил руками.
  const [detected, setDetected] = useState<string | null>(null);

  const detect = async () => {
    setHint(null);
    setDetecting(true);
    try {
      const pos = await requestUserLocation();
      if (!pos) { setHint('Твоя версия Telegram не умеет отдавать геопозицию.'); return; }
      const found = cityFromCoords(pos.lat, pos.lon);
      if (!found) { setHint('Рядом нет города из справочника — впиши вручную.'); return; }
      setDetected(found);
      onCity(found);
    } catch {
      setHint('Доступ к геопозиции не выдан — впиши город вручную.');
    } finally {
      setDetecting(false);
    }
  };

  const mismatch = detected !== null && canonicalCity(city) !== detected;

  return (
    <>
      {labels && <label className="field-label" htmlFor={`${uid}-city`}>Город</label>}
      <input
        id={`${uid}-city`}
        className="field-input"
        list={cityListId}
        autoComplete="off"
        placeholder={labels ? undefined : 'Город'}
        value={city}
        onChange={(e) => onCity(e.target.value)}
      />
      <datalist id={cityListId}>
        {CITIES.map((c) => <option key={c} value={c} />)}
      </datalist>

      {detectable && (
        <>
          <button
            type="button"
            className="big-cta secondary"
            style={{ minHeight: 40, marginTop: 6, fontSize: 14 }}
            onClick={detect}
            disabled={detecting}
          >
            {detecting ? 'Определяем…' : '📍 Определить по геопозиции'}
          </button>
          {hint && <div className="field-hint">{hint}</div>}
          {/* Предупреждение, а не запрет: курьер мог отправить отчёт вечером из дома, уже
              уехав из района, где работал. Решает он. */}
          {mismatch && (
            <div className="field-hint" style={{ color: 'var(--bk-destructive)' }}>
              Геопозиция говорит «{detected}», а выбран «{city}». Проверь, если это ошибка.
            </div>
          )}
        </>
      )}

      {labels && (
        <label className="field-label" htmlFor={`${uid}-district`} style={{ marginTop: 6 }}>
          Район
        </label>
      )}
      <input
        id={`${uid}-district`}
        className="field-input"
        list={districtListId}
        autoComplete="off"
        placeholder={labels ? undefined : districtPlaceholder}
        value={district}
        onChange={(e) => onDistrict(e.target.value)}
        style={{ marginTop: 6 }}
      />
      <datalist id={districtListId}>
        {districts.map((d) => <option key={d} value={d} />)}
      </datalist>
    </>
  );
}
